import React from 'react';
import { TrendingUp, AlertCircle, Calendar, Users } from 'lucide-react';

const PredictiveAnalyticsPanel = ({ trends }) => {
  // Calculate predictions based on trends
  const calculatePrediction = () => {
    const recentTrends = trends?.slice(-3);
    const avgOnboarding = recentTrends?.reduce((sum, t) => sum + t?.onboarding, 0) / recentTrends?.length;
    const avgOffboarding = recentTrends?.reduce((sum, t) => sum + t?.offboarding, 0) / recentTrends?.length;
    
    return {
      nextMonthOnboarding: Math.round(avgOnboarding * 1.1),
      nextMonthOffboarding: Math.round(avgOffboarding * 0.9),
      capacityUtilization: 78.5,
      retentionRisk: 'Low'
    };
  };

  const prediction = calculatePrediction();

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-500/10 rounded-lg">
            <TrendingUp className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Predictive Analytics</h2>
            <p className="text-sm text-muted-foreground">Forecast capacity requirements and retention risks</p>
          </div>
        </div>

        {/* Prediction Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">Next Month Forecast</span>
            </div>
            <p className="text-2xl font-bold text-blue-500">{prediction?.nextMonthOnboarding}</p>
            <p className="text-xs text-muted-foreground">New hires expected</p>
          </div>
          <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-muted-foreground">Expected Exits</span>
            </div>
            <p className="text-2xl font-bold text-orange-500">{prediction?.nextMonthOffboarding}</p>
            <p className="text-xs text-muted-foreground">Projected departures</p>
          </div>
          <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-muted-foreground">Capacity Utilization</span>
            </div>
            <p className="text-2xl font-bold text-purple-500">{prediction?.capacityUtilization}%</p>
            <p className="text-xs text-muted-foreground">HR team capacity</p>
          </div>
          <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-success" />
              <span className="text-xs text-muted-foreground">Retention Risk</span>
            </div>
            <p className="text-2xl font-bold text-success">{prediction?.retentionRisk}</p>
            <p className="text-xs text-muted-foreground">Risk level</p>
          </div>
        </div>

        {/* Trend Visualization */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Historical Trends & Forecast</h3>
          <div className="flex items-end justify-between gap-2 h-48">
            {trends?.map((item, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full relative" style={{ height: '160px' }}>
                  {/* Onboarding Bar */}
                  <div
                    className="absolute bottom-0 w-[45%] left-0 bg-blue-500 rounded-t-lg transition-all"
                    style={{ height: `${(item?.onboarding / 20) * 100}%` }}
                  ></div>
                  {/* Offboarding Bar */}
                  <div
                    className="absolute bottom-0 w-[45%] right-0 bg-orange-500 rounded-t-lg transition-all"
                    style={{ height: `${(item?.offboarding / 20) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-muted-foreground text-center">{item?.month?.split(' ')?.[0]}</span>
              </div>
            ))}
            {/* Forecast Bar */}
            <div className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full relative" style={{ height: '160px' }}>
                <div
                  className="absolute bottom-0 w-[45%] left-0 bg-blue-500/50 border-2 border-blue-500 border-dashed rounded-t-lg transition-all"
                  style={{ height: `${(prediction?.nextMonthOnboarding / 20) * 100}%` }}
                ></div>
                <div
                  className="absolute bottom-0 w-[45%] right-0 bg-orange-500/50 border-2 border-orange-500 border-dashed rounded-t-lg transition-all"
                  style={{ height: `${(prediction?.nextMonthOffboarding / 20) * 100}%` }}
                ></div>
              </div>
              <span className="text-xs text-muted-foreground text-center">Feb (Forecast)</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-xs text-muted-foreground">Onboarding</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-xs text-muted-foreground">Offboarding</span>
            </div>
          </div>
        </div>

        {/* Insights & Recommendations */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">AI-Powered Insights</h3>
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Capacity Planning</p>
                <p className="text-sm text-muted-foreground">
                  Based on current trends, your HR team will be at 78.5% capacity next month. Consider allocating additional resources for onboarding support.
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Retention Outlook</p>
                <p className="text-sm text-muted-foreground">
                  Low retention risk detected. Exit patterns are within normal range. Continue monitoring exit interview feedback for early warning signs.
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Seasonal Trend Alert</p>
                <p className="text-sm text-muted-foreground">
                  Onboarding volume typically increases by 15% in Q1. Prepare workflow templates and documentation in advance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalyticsPanel;